import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import type { QuickAction } from '@constants/dashboard';
import { cn } from '@/lib/utils';

const QuickActionCard: React.FC<{ action: QuickAction }> = ({ action }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { icon: Icon, path, labelKey, descriptionKey } = action;

  return (
    <button
      type="button"
      onClick={() => navigate(path)}
      className={cn(
        'group relative flex w-full items-start gap-3 rounded-xl border bg-card p-4 pr-8 text-left text-card-foreground shadow-sm',
        'transition-[background-color,border-color,transform] duration-150 ease-shell',
        'hover:border-primary/30 hover:bg-accent/40 active:scale-[0.99]',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
        'motion-reduce:transition-none motion-reduce:active:scale-100',
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
        <Icon className="size-[18px]" strokeWidth={1.9} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium leading-snug text-balance">{t(labelKey)}</span>
        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground text-pretty">
          {t(descriptionKey)}
        </span>
      </span>

      <ArrowUpRight
        aria-hidden="true"
        className="absolute right-3 top-3.5 size-3.5 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      />
    </button>
  );
};

export default QuickActionCard;
